'use client'

import * as React from 'react'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { cn } from '@/lib/utils'

/**
 * RadioGroup / RadioGroupItem — Radix-backed, replaces hand-rolled
 * <input type="radio">. Arrow-key roving focus comes from Radix. Compose
 * each item in a row with a Label (htmlFor ↔ id) at min-h-11 (44px rule).
 * Inside a fieldset, point the group at the legend with aria-labelledby —
 * the Radix root is a div, not a fieldset.
 */
const RadioGroup = React.forwardRef<
    React.ElementRef<typeof RadioGroupPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
    <RadioGroupPrimitive.Root ref={ref} className={cn('grid gap-1', className)} {...props} />
))
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

const RadioGroupItem = React.forwardRef<
    React.ElementRef<typeof RadioGroupPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
    <RadioGroupPrimitive.Item
        ref={ref}
        className={cn(
            'h-4 w-4 shrink-0 rounded-full border border-[var(--border-default)] bg-[var(--bg-surface)] transition-colors duration-200',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-200)] focus-visible:border-[var(--border-focus)]',
            'disabled:cursor-not-allowed disabled:opacity-50',
            // Quiet checked state: brand border + inner dot, no filled ring
            'data-[state=checked]:border-[var(--primary-600)]',
            className
        )}
        {...props}
    >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
            <span className="h-2 w-2 rounded-full bg-[var(--bg-brand)]" />
        </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
))
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export { RadioGroup, RadioGroupItem }
